/**
 * ── MILESTONE 2 · PHASE 2: THE LEFT PANEL FOLLOWS THE TEMPLATE (Layne, 2026-10-07) ──────
 *
 * Layne's answers (`REPLY-JACK-M2-LEFT-PANEL-20261007.md`) to Jack's proposal
 * (`_share/M2-LEFT-PANEL-GUARDRAILS-FOR-LAYNE-20261006.md`). Everything here is READ OFF
 * SFMC — the `ECH_TPL_*` definition and each block's own @GUARDRAILS — so Layne changes
 * behaviour by editing Content Builder and the app only follows.
 *
 * ON THE TEMPLATE (`ECH_TPL_*`):
 *   "palette": ["ECH_Featured1CTA", "ECH_ListingTable", "ECH_Amenities", "ECH_GetDirections"]
 *       Which chips the left panel offers. Naming any member of a family offers the family.
 *       No key = every chip (phase 1); [] = nothing can be added.
 *   "editableBetween": { "after": [..first present wins..], "before": "ECH_ContactUsDefault" }
 *       The play area. Blocks above the cutoff and from `before` down stay put; adds land only
 *       inside it. Any ECH_SectionSpacer25 directly under the cutoff belongs to the locked top.
 *   "familyDefault": "ECH_Featured1CTA"   (a name or a list of names)
 *       Which member a family chip places when the template does not already use the family.
 *   "editableBetween": { …, "maxModules": 3, "countAsOne": [["ECH_Featured1CTA", "ECH_Featured2CTA"]],
 *                        "notCounted": ["ECH_TableHeader", "ECH_SectionSpacer50", "ECH_SectionSpacer25"],
 *                        "maxModulesMessage": "This section holds up to 3 modules. …" }
 *       (Layne, ASK-JACK-PLAY-AREA-MAX-3-20261015) At most N modules inside the play area. A
 *       run of blocks from one `countAsOne` group counts once; `notCounted` blocks are skipped,
 *       for the count AND for run continuity (a header or spacer never splits a run). Only a
 *       change that RAISES the count past N is refused — an email already over keeps what it
 *       has. A new block of a group that already has a run lands at the end of that run.
 *
 * ON A BLOCK (@GUARDRAILS):
 *   "addsWith": { "block": "ECH_TableHeader", "position": "above", "unlessPresent": true }
 *       Adding this block brings that block with it when the section has none yet. Deleting
 *       the last block of the section takes the header with it (Layne to confirm, 10-07).
 *   "spacingAbove": { "block": "ECH_SectionSpacer50", "unlessAbove": ["ECH_TableHeader", …] }
 *       After every add, move or delete inside the play area, a bar section gets that spacer
 *       directly above it unless the block above is one of `unlessAbove`; a spacer left between
 *       two bars, or no longer above a bar, is removed.
 *
 * PURE: no React, no registry imports. The Studio passes `nameOf` (type → SFMC name) and
 * `grOf` (type → the block's full guardrails); tests pass plain functions.
 */

export interface M2Block { id: string; type: string }

/** the toast when a move or add would leave the play area */
export const M2_LOCKED_MSG = "This part of the email stays where it is.";
export type NameOf = (type: string) => string;
export type GrOf = (type: string) => Record<string, unknown> | null | undefined;

const SPACER25 = "ECH_SectionSpacer25";
const strList = (v: unknown): string[] =>
  (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]).map((x) => String(x || "").trim()).filter(Boolean);
const lc = (s: string) => String(s || "").toLowerCase();

/* ── THE TEMPLATE'S KEYS ─────────────────────────────────────────────────────────────── */

export interface M2TplRules {
  /** chip names the panel offers; null = no `palette` key (every chip) */
  palette: string[] | null;
  /** editableBetween.after, in priority order (empty = no top cutoff) */
  after: string[];
  /** editableBetween.before (empty = no bottom cutoff) */
  before: string[];
  /** familyDefault names */
  familyDefault: string[];
  /** true when the template declares palette or editableBetween — the phase-2 switch */
  active: boolean;
  /** editableBetween.maxModules; null = no cap */
  maxModules: number | null;
  /** editableBetween.countAsOne — groups whose consecutive members count once */
  countAsOne: string[][];
  /** editableBetween.notCounted — skipped for the count and for run continuity */
  notCounted: string[];
  /** the refusal shown when the area is full */
  maxModulesMessage: string;
}

/** read the phase-2 keys off one raw `ECH_TPL_*` definition */
export function m2TplRules(def: Record<string, unknown> | null | undefined): M2TplRules {
  const d = def || {};
  const pal = Object.prototype.hasOwnProperty.call(d, "palette") ? strList(d.palette) : null;
  const eb = (d.editableBetween && typeof d.editableBetween === "object" ? d.editableBetween : {}) as Record<string, unknown>;
  const after = strList(eb.after);
  const before = strList(eb.before);
  const mmRaw = Number(eb.maxModules);
  const maxModules = eb.maxModules != null && eb.maxModules !== "" && Number.isFinite(mmRaw) && mmRaw >= 0 ? Math.floor(mmRaw) : null;
  const countAsOne = (Array.isArray(eb.countAsOne) ? eb.countAsOne : [])
    .map((g) => strList(g))
    .filter((g) => g.length > 0);
  const msg = String(eb.maxModulesMessage || "").trim();
  return {
    palette: pal,
    after, before,
    familyDefault: strList(d.familyDefault),
    active: pal !== null || after.length > 0 || before.length > 0,
    maxModules,
    countAsOne,
    notCounted: strList(eb.notCounted),
    maxModulesMessage: msg || (maxModules != null ? "This section holds up to " + maxModules + " modules. Remove one before adding another." : ""),
  };
}

/* ── THE PLAY AREA ───────────────────────────────────────────────────────────────────── */

/**
 * `lo` = the first index inside the play area, `hi` = the index of the first locked block
 * at the bottom. Insertion indexes lo..hi are allowed; blocks lo..hi-1 move.
 * After: the FIRST name in `after` present in the doc wins (Layne: if the community name is
 * deleted, whatever sat above it becomes the cutoff); any ECH_SectionSpacer25 directly under
 * it is part of the locked top. None present → lo = 0. Before: the first `before` block at or
 * below lo; none present → the end.
 */
export function m2PlayArea(doc: M2Block[], nameOf: NameOf, r: M2TplRules): { lo: number; hi: number } {
  const names = doc.map((b) => lc(nameOf(b.type)));
  let lo = 0;
  for (const a of r.after) {
    const i = names.indexOf(lc(a));
    if (i > -1) { lo = i + 1; break; }
  }
  while (lo < doc.length && names[lo] === lc(SPACER25)) lo++;
  let hi = doc.length;
  if (r.before.length) {
    const want = new Set(r.before.map(lc));
    for (let i = lo; i < doc.length; i++) if (want.has(names[i])) { hi = i; break; }
  }
  return { lo, hi: Math.max(lo, hi) };
}

/** the ids that sit outside the play area — pinned for the arrows and the drag handle */
export function m2LockedIds(doc: M2Block[], nameOf: NameOf, r: M2TplRules): Set<string> {
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  return new Set(doc.filter((_, i) => i < lo || i >= hi).map((b) => b.id));
}

/** true when `a` and `b` leave the SAME blocks outside the play area — a move that widened
 *  into a unit (a header run) must not carry anything across the cutoff (2026-10-27) */
export function m2SameLocked(a: M2Block[], b: M2Block[], nameOf: NameOf, r: M2TplRules): boolean {
  const x = m2LockedIds(a, nameOf, r), y = m2LockedIds(b, nameOf, r);
  if (x.size !== y.size) return false;
  for (const id of Array.from(x)) if (!y.has(id)) return false;
  return true;
}

/** may `fromId` move to insertion index `to` (index in the doc WITHOUT it — reorder's contract)? */
export function m2CanMove(doc: M2Block[], nameOf: NameOf, r: M2TplRules, fromId: string, to: number): boolean {
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  const i = doc.findIndex((b) => b.id === fromId);
  if (i < lo || i >= hi) return false;
  return to >= lo && to <= hi - 1;
}

/** the nearest allowed insertion index to `at` */
export function m2ClampInsert(doc: M2Block[], nameOf: NameOf, r: M2TplRules, at: number): number {
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  return Math.max(lo, Math.min(hi, at));
}

/* ── THE PANEL ───────────────────────────────────────────────────────────────────────── */

/** does `palette` offer this chip (any member of its family named)? null palette = yes */
export function m2PaletteOffers(r: M2TplRules, members: string[]): boolean {
  if (!r.palette) return true;
  const want = new Set(r.palette.map(lc));
  return members.some((m) => want.has(lc(m)));
}

/** the member a family chip places by the template's familyDefault, or null */
export function m2FamilyDefault(r: M2TplRules, members: string[]): string | null {
  const want = new Set(r.familyDefault.map(lc));
  return members.find((m) => want.has(lc(m))) || null;
}

/* ── addsWith ────────────────────────────────────────────────────────────────────────── */

export interface AddsWith { block: string; position: "above"; unlessPresent: boolean }
export function addsWithOf(gr: Record<string, unknown> | null | undefined): AddsWith | null {
  const a = gr && (gr.addsWith as Record<string, unknown> | undefined);
  if (!a || typeof a !== "object" || !a.block) return null;
  return { block: String(a.block), position: "above", unlessPresent: a.unlessPresent !== false };
}

/**
 * Should inserting a block of `type` at `at` also insert its `addsWith` block above it?
 * "Present" = walking UP from the insertion point over blocks of the same section (any block
 * whose own addsWith names the same header) and plain dividers, the next block is the header.
 */
export function m2NeedsCompanion(doc: M2Block[], nameOf: NameOf, grOf: GrOf, type: string, at: number): string | null {
  const aw = addsWithOf(grOf(type));
  if (!aw) return null;
  if (!aw.unlessPresent) return aw.block;
  const head = lc(aw.block);
  for (let i = at - 1; i >= 0; i--) {
    const t = doc[i].type;
    const n = lc(nameOf(t));
    if (n === head) return null;
    const own = addsWithOf(grOf(t));
    if ((own && lc(own.block) === head) || /^ech_dividerline$/.test(n)) continue;
    break;
  }
  return aw.block;
}

/**
 * Headers (any block some other block's addsWith names) inside the play area that no longer
 * sit directly above a block of their section — removed after a user's delete or move.
 */
export function m2DropOrphanHeads<T extends M2Block>(doc: T[], nameOf: NameOf, grOf: GrOf, r: M2TplRules, libraryHeads: string[] = []): T[] {
  /* the headers are known from the LIBRARY (every block's addsWith), not just this doc —
     once the last card is deleted, nothing left in the doc names the header any more */
  const heads = new Set<string>(libraryHeads.map(lc));
  doc.forEach((b) => { const a = addsWithOf(grOf(b.type)); if (a) heads.add(lc(a.block)); });
  if (!heads.size) return doc;
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  return doc.filter((b, i) => {
    if (i < lo || i >= hi) return true;
    const n = lc(nameOf(b.type));
    if (!heads.has(n)) return true;
    const next = doc[i + 1];
    const own = next ? addsWithOf(grOf(next.type)) : null;
    return !!(own && lc(own.block) === n);
  });
}

/* ── spacingAbove ────────────────────────────────────────────────────────────────────── */

export interface SpacingAbove { block: string; unlessAbove: string[] }
export function spacingAboveOf(gr: Record<string, unknown> | null | undefined): SpacingAbove | null {
  const s = gr && (gr.spacingAbove as unknown);
  if (!s) return null;
  if (typeof s === "string") return { block: s, unlessAbove: [] };
  if (typeof s !== "object") return null;
  const o = s as Record<string, unknown>;
  if (!o.block) return null;
  return { block: String(o.block), unlessAbove: strList(o.unlessAbove) };
}

/**
 * Re-space the play area. `makeSpacer(name)` returns a fresh block for a spacer name (or null
 * when that block is not in the library — then nothing is inserted). Only spacers NAMED by a
 * spacingAbove declaration are ever removed, and only inside the play area.
 */
export function m2Respace<T extends M2Block>(doc: T[], nameOf: NameOf, grOf: GrOf, r: M2TplRules, makeSpacer: (name: string) => T | null): T[] {
  const managed = new Set<string>();
  doc.forEach((b) => { const s = spacingAboveOf(grOf(b.type)); if (s) managed.add(lc(s.block)); });
  if (!managed.size) return doc;
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  const head = doc.slice(0, lo);
  const mid = doc.slice(lo, hi);
  const tail = doc.slice(hi);
  /* 1. drop every managed spacer in the area; 2. put back exactly the ones the rule asks for */
  const bare = mid.filter((b) => !managed.has(lc(nameOf(b.type))));
  const out: T[] = [];
  const prevName = (k: number) => (k > 0 ? lc(nameOf(out[k - 1].type)) : head.length ? lc(nameOf(head[head.length - 1].type)) : "");
  for (const b of bare) {
    const s = spacingAboveOf(grOf(b.type));
    const above = prevName(out.length);
    if (s && above && !s.unlessAbove.map(lc).includes(above)) {
      const sp = makeSpacer(s.block);
      if (sp) out.push(sp);
    }
    out.push(b);
  }
  /* keep the spacers that were already right (same position) so their ids survive */
  const keep = mid.filter((b) => managed.has(lc(nameOf(b.type))));
  const reused = out.map((b) => {
    if (!managed.has(lc(nameOf(b.type))) || !keep.length) return b;
    const i = keep.findIndex((k) => lc(nameOf(k.type)) === lc(nameOf(b.type)));
    return i > -1 ? keep.splice(i, 1)[0] : b;
  });
  const next = [...head, ...reused, ...tail];
  const same = next.length === doc.length && next.every((b, i) => b === doc[i]);
  return same ? doc : next;
}

/* ── maxModules (Layne, 2026-10-15) ─────────────────────────────────────────────────── */

/** the countAsOne group index a name belongs to, or -1 */
function groupOf(r: M2TplRules, name: string): number {
  const n = lc(name);
  return r.countAsOne.findIndex((g) => g.some((m) => lc(m) === n));
}

/** modules in the play area: notCounted skipped, a run of one countAsOne group counts once */
export function m2ModuleCount(doc: M2Block[], nameOf: NameOf, r: M2TplRules): number {
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  const skip = new Set(r.notCounted.map(lc));
  let count = 0;
  let prevGroup = -2;            /* -2 = nothing counted yet; -1 = a plain module */
  for (let i = lo; i < hi; i++) {
    const n = nameOf(doc[i].type);
    if (skip.has(lc(n))) continue;
    const g = groupOf(r, n);
    if (g > -1 && g === prevGroup) continue;   /* the run already counted */
    count++;
    prevGroup = g;
  }
  return count;
}

/**
 * The refusal when `next` (the doc after an add, move or duplicate) would put more modules in
 * the play area than the template allows AND more than `cur` had; otherwise null. Comparing to
 * `cur` keeps an email that is already over the cap editable (nothing gets worse).
 */
export function m2ModulesBlocked(cur: M2Block[], next: M2Block[], nameOf: NameOf, r: M2TplRules): string | null {
  if (r.maxModules == null) return null;
  const after = m2ModuleCount(next, nameOf, r);
  if (after <= r.maxModules) return null;
  return after > m2ModuleCount(cur, nameOf, r) ? r.maxModulesMessage : null;
}

/**
 * Where a new block of `name` should land so it joins its group's run: the index just after
 * the last member of the FIRST run of that group inside the play area. -1 when the name is in
 * no group or the group has no run there yet.
 */
export function m2RunEnd(doc: M2Block[], nameOf: NameOf, r: M2TplRules, name: string): number {
  const g = groupOf(r, name);
  if (g < 0) return -1;
  const { lo, hi } = m2PlayArea(doc, nameOf, r);
  const skip = new Set(r.notCounted.map(lc));
  let end = -1;
  for (let i = lo; i < hi; i++) {
    const n = nameOf(doc[i].type);
    if (groupOf(r, n) === g) { end = i + 1; continue; }
    if (end > -1 && !skip.has(lc(n))) break;   /* the first run ended */
  }
  return end;
}

/**
 * The panel's greyed-out reason for a chip (its family `members`) when the area is full: null
 * when there is no cap, the area has room, or a member's group already has a run to join.
 */
export function m2PanelFull(doc: M2Block[], nameOf: NameOf, r: M2TplRules, members: string[]): string | null {
  if (r.maxModules == null) return null;
  if (m2ModuleCount(doc, nameOf, r) < r.maxModules) return null;
  if (members.some((m) => m2RunEnd(doc, nameOf, r, m) > -1)) return null;
  return r.maxModulesMessage;
}
